import { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw, ListChecks,
} from 'lucide-react';

import { cardClearingApi } from '@/api/cardClearing.api';
import { Spinner }         from '@/components/ui/Spinner';
import { ListPager } from '@/components/lists/ListPager';
import { ListSearch, DateRangeFilter } from '@/components/lists/ListFilters';
import { cn }              from '@/utils/cn';

import type {
  CardClearingItemDto,
  CardClearingItemFilterDto,
} from '@/types/cardReconciliation.types';

const STATUS_COLORS: Record<string, string> = {
  PendingSettlement: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
  PartiallySettled:  'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
  Settled:           'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  Overdue:           'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400',
  Chargeback:        'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
  Reversed:          'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400',
};

const STATUS_LABELS: Record<string, string> = {
  PendingSettlement: 'Awaiting settlement',
  PartiallySettled:  'Settled with a difference',
  Settled:           'Settled',
  Overdue:           'Overdue',
  Chargeback:        'Chargeback',
  Reversed:          'Cancelled sale',
};

export const CardClearingListPage = () => {
  const [items,   setItems]   = useState<CardClearingItemDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(1);
  const pageSize = 50;

  const [search,   setSearch]   = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');

  const load = useCallback(async (filter: CardClearingItemFilterDto) => {
    setLoading(true);
    try {
      const res  = await cardClearingApi.getList(filter);
      const data = res.data?.data;
      if (data) {
        setItems(data.items ?? []);
        setTotal(data.totalCount ?? 0);
      }
    } catch (err) {
      console.error('Failed to load clearing items', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const buildFilter = useCallback((): CardClearingItemFilterDto => ({
    search:   search || undefined,
    dateFrom: dateFrom || undefined,
    dateTo:   dateTo   || undefined,
    page,
    pageSize,
  }), [search, dateFrom, dateTo, page]);

  useEffect(() => { load(buildFilter()); }, [page]); // eslint-disable-line

  const handleSearch = () => {
    setPage(1);
    load({ search: search || undefined, dateFrom: dateFrom || undefined, dateTo: dateTo || undefined, page: 1, pageSize });
  };

  const fmtDate = (d: string) =>
    new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-white">Card Clearing Items</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{total.toLocaleString()} total records</p>
          </div>
          <button onClick={() => load(buildFilter())} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400" title="Refresh">
            <RefreshCw size={16} />
          </button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mt-4 flex-wrap">
          <ListSearch
            value={search}
            onChange={setSearch}
            onSubmit={handleSearch}
            placeholder="Search batch, terminal or bill number"
          />
          <div className="flex items-center gap-2 ml-auto">
            <DateRangeFilter
              label="Sale date"
              from={dateFrom || undefined}
              to={dateTo || undefined}
              onChange={({ from, to }) => { setDateFrom(from ?? ''); setDateTo(to ?? ''); }}
            />
            <button onClick={handleSearch} className="px-3 py-1.5 text-sm bg-teal-600 hover:bg-teal-700 text-white rounded-lg">Search</button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-400">
            <ListChecks size={40} className="mb-3 opacity-40" />
            <p className="text-sm font-medium">No clearing items found</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800/80 backdrop-blur border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Acquirer</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Card Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Bill</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Terminal / Batch</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Card / Approval</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Sale Date</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Gross Amount</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">MDR</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Net Expected</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {items.map(item => (
                <tr key={item.id} className="hover:bg-teal-50 dark:hover:bg-slate-700/50 transition-colors">
                  <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">{item.acquirerName}</td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{item.cardTypeName}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-600 dark:text-slate-300">{billNumber(item.sourceDocumentNo)}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500 dark:text-slate-400">
                    <div>{item.terminalId ?? '-'}</div>
                    <div className="text-[10px] text-slate-400">{item.batchNumber ?? '-'}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500 dark:text-slate-400">
                    <div>{item.cardLast4 ? `**** ${item.cardLast4}` : '-'}</div>
                    <div className="text-[10px] text-slate-400">{item.approvalCode ?? '-'}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700 dark:text-slate-300">{fmtDate(item.saleDate)}</td>
                  <td className="px-4 py-3 text-right font-mono">{item.grossAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-500 dark:text-gray-400">{item.expectedMdrAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                  <td className="px-4 py-3 text-right font-mono">{item.expectedNetAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', STATUS_COLORS[item.statusName] ?? 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400')}>
                      {STATUS_LABELS[item.statusName] ?? item.statusName}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <ListPager
          page={page}
          totalPages={totalPages}
          totalCount={total}
          onChange={setPage}
          noun="items"
        />
      )}
    </div>
  );
};

/** The stored key is "{store}/{bill}" for matching; people only need the bill number. */
function billNumber(sourceDocumentNo?: string | null): string {
  if (!sourceDocumentNo) return '-';
  const i = sourceDocumentNo.lastIndexOf('/');
  return i >= 0 ? sourceDocumentNo.slice(i + 1) : sourceDocumentNo;
}
